import { inject, Injectable } from '@angular/core';
import { NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { DestroyRef } from '@angular/core';

import { LoggerService } from '../logger/logger.service';

@Injectable({
  providedIn: 'root'
})
export class RouterObserverService {
  private readonly router = inject(Router);
  private readonly logger = inject(LoggerService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.observeRouterEvents();
  }

  private observeRouterEvents(): void {
    // Log all navigation events for debugging
    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event instanceof NavigationStart) {
          this.logger.log('Router Navigation Start', {
            url: event.url,
            navigationTrigger: event.navigationTrigger,
            restoredState: event.restoredState
          }, {
            when: ['debugRouter'],
            hideWhen: ['prod']
          });
        } else if (event instanceof NavigationEnd) {
          this.logger.log('Router Navigation End', {
            url: event.url,
            urlAfterRedirects: event.urlAfterRedirects
          }, {
            when: ['debugRouter'],
            hideWhen: ['prod']
          });
        }
      });

    // Log 404 errors
    this.router.events
      .pipe(
        filter((event): event is NavigationError => event instanceof NavigationError),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event) => {
        const currentUrl = this.router.url;
        const attemptedUrl = event.url;

        this.logger.log('Router 404 Error Detected', {
          currentLocation: currentUrl,
          attemptedDestination: attemptedUrl,
          error: event.error?.message || 'Unknown navigation error',
          errorType: event.error?.name || 'Unknown'
        }, {
          when: ['debugRouter', 'prod'],
          hideWhen: [],
          logInSentry: true
        });
      });
  }
}
